import { z } from 'zod';
import { trackAction } from '../../analytics/mutation-events';
import { noteLeaving } from '../../api/billing-leaving';
import type { CompanyApi } from '../../api/company-api';
import type { BillingLink } from '../../api/types';

/** The paid plans a sign-up can start on; Free needs no choice and Enterprise is a partner's quote. */
export const startablePlans = ['starter', 'pro', 'business'] as const;
export type StartablePlan = (typeof startablePlans)[number];

/** The plan picked on the pricing page, carried through sign-up to its checkout. */
export interface ChosenPlan {
  pkg: StartablePlan;
  interval: 'month' | 'year';
}

/**
 * What `/sign-up` may carry from the pricing page's "Start with {plan}": the plan and the interval
 * shown beside it. Anything else (Free, Enterprise, a stale or typed value) is dropped, never the page.
 */
export const chosenPlanSearch = {
  plan: z.enum(startablePlans).optional().catch(undefined),
  interval: z.enum(['month', 'year']).optional().catch(undefined),
};

/** The chosen plan from the address, or null for a sign-up that starts on Free. */
export function chosenPlanOf(plan?: StartablePlan, interval?: 'month' | 'year'): ChosenPlan | null {
  return plan ? { pkg: plan, interval: interval ?? 'month' } : null;
}

/** The pricing page's link for a plan: Free signs up bare, a paid plan carries itself and its interval. */
export function signUpSearchFor(
  key: string,
  interval: 'month' | 'year',
): { plan?: StartablePlan; interval?: 'month' | 'year' } {
  const startable = (startablePlans as readonly string[]).includes(key);
  return startable ? { plan: key as StartablePlan, interval } : {};
}

/**
 * Starts the chosen plan's checkout for the company just made: the same hosted checkout Billing's
 * plan choice uses for a first paid plan (a new company is on Free with no card plan), noted so the
 * billing page waits for the plan on the way back. True when the browser is on its way.
 */
export async function startChosenPlan(
  company: Pick<CompanyApi, 'checkout'>,
  tenantId: string,
  chosen: ChosenPlan,
  go: (link: BillingLink) => void = (link) => window.location.assign(link.url),
): Promise<boolean> {
  try {
    const link = await company.checkout(tenantId, chosen.pkg, undefined, {
      interval: chosen.interval,
    });
    trackAction('billing.checkout_started', { plan: chosen.pkg, from: 'sign-up' });
    noteLeaving(tenantId, { kind: 'plan', from: 'free', to: chosen.pkg, effectiveOn: null });
    go(link);
    return true;
  } catch {
    return false;
  }
}
